import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    Input,
    OnInit,
    AfterViewInit,
    OnDestroy,
    ViewChild,
    ElementRef
} from '@angular/core';
import { Align } from 'app/enums';
import { BehaviorSubject, debounceTime, map, merge, of, switchMap } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { GetMeasurementResponse } from '@services/api-client/responses';
import { ApiClientService } from '@services/api-client/api-client.service';
import { FormGroup } from '@angular/forms';
import { FilterConfig } from 'app/features/shared/component/filter/filter-model';
import { FilterComponent, TableType } from 'app/features/shared/component/filter/filter.component';
import { MeasurementSelectionService } from '../../services/measurement-selection.service';
import { TablePreferencesService } from '@services/local-storage/table-preferences.service';
import { FilterRequest } from '@services/api-client/requests';

@UntilDestroy()
@Component({
    selector: 'app-select-measurement',
    templateUrl: './select-measurement.component.html',
    styleUrl: './select-measurement.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SelectMeasurementComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild(FilterComponent) public readonly filterComponent: FilterComponent;
    @Input() public selectedMeasurementId: string;
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly apiClientService = inject(ApiClientService);
    private readonly measurementSelectionService = inject(MeasurementSelectionService);
    private readonly tablePreferencesService = inject(TablePreferencesService);
    private readonly elementRef = inject(ElementRef);

    private static readonly tableId = 'measurement-list' as const;
    public readonly align = Align;
    public isLoading = false;
    public sortKey: string = 'created';
    public sortAscending = false;
    public currentPage = 1;
    public pageSize = 200;
    public searchPhrase: string = '';
    public filterForm: FormGroup;
    public readonly tableType = TableType.Measurements;
    public filterConfig: FilterConfig = {
        fields: ['users', 'devices']
    };

    private readonly getMeasurementSubject = new BehaviorSubject<void>(undefined);
    private resizeObserver: ResizeObserver | null = null;

    public measurements$ = this.getMeasurementSubject.pipe(
        debounceTime(250),
        switchMap(() => {
            // Guard against filterComponent being undefined during initialization
            if (!this.filterComponent) {
                return of([]);
            }

            const predicates = this.filterComponent.getPredicates();
            const filterRequest: FilterRequest = {
                page: this.currentPage,
                pageSize: this.pageSize,
                predicates,
                search: this.searchPhrase || undefined,
                sortKey: this.sortKey,
                sortAscending: this.sortAscending
            };

            return merge(
                of(null),
                this.apiClientService.getMeasurements$(filterRequest).pipe(
                    map((response) => {
                        return response.items;
                    }),
                    switchMap((measurements: GetMeasurementResponse[]) => {
                        return of(measurements);
                    })
                )
            );
        })
    );

    public ngOnInit() {
        const saved = this.tablePreferencesService.getPreferences(SelectMeasurementComponent.tableId);
        if (saved?.sort) {
            this.sortKey = saved.sort.key;
            this.sortAscending = saved.sort.ascending;
        }

        // Restore pagination preferences
        if (saved?.currentPage !== undefined) {
            this.currentPage = saved.currentPage;
        }
        if (saved?.pageSize !== undefined) {
            this.pageSize = saved.pageSize;
        }
        if (saved?.searchPhrase) {
            this.searchPhrase = saved.searchPhrase;
        }

        this.measurementSelectionService.selectedMeasurementId$.pipe(untilDestroyed(this)).subscribe((id) => {
            if (id !== this.selectedMeasurementId && id) {
                this.selectedMeasurementId = id;
            }
            this.changeDetectorRef.markForCheck();
        });

        this.measurementSelectionService.loading$.pipe(untilDestroyed(this)).subscribe((loading) => {
            this.isLoading = loading;
            this.changeDetectorRef.markForCheck();
        });
    }

    public ngAfterViewInit(): void {
        // Trigger initial fetch now that filterComponent is available
        this.getMeasurementSubject.next();

        // Subscribe to measurements to scroll selected item into view
        this.measurements$.pipe(untilDestroyed(this)).subscribe(() => {
            this.scrollSelectedIntoView();
        });

        // Set up ResizeObserver to detect when sidebar becomes visible
        const containerElement = this.elementRef.nativeElement as HTMLElement;
        this.resizeObserver = new ResizeObserver(() => {
            if (containerElement.offsetHeight > 0) {
                this.scrollSelectedIntoView();
            }
        });
        this.resizeObserver.observe(containerElement);
    }

    public ngOnDestroy(): void {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }

    private scrollSelectedIntoView(): void {
        if (!this.selectedMeasurementId) {
            return;
        }

        // Use setTimeout to ensure view has rendered
        setTimeout(() => {
            const containerElement = this.elementRef.nativeElement as HTMLElement;
            // Query through the app-table and table elements to find the selected row
            const selectedRow = containerElement.querySelector('table tbody tr.selected');
            if (selectedRow) {
                selectedRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 0);
    }

    public showMeasurement(measurementId: string): void {
        if (this.isLoading) return;
        this.measurementSelectionService.selectMeasurement(measurementId);
        this.changeDetectorRef.markForCheck();
    }

    public isSelected(measurementId: string): boolean {
        return this.selectedMeasurementId === measurementId;
    }
}
